import { Component, Prop, h, State, Element, Watch, Fragment } from '@stencil/core';
import state, { onChange } from '../store/globalStore';
import { eventBus } from '../utils/eventBus';
import Icon from '../../ui-components/Icon/Icon';
import { Button } from '../../ui-components/Button';
import { consumerAuth } from '../utils/consumerAuth';
import { BaseModalContainer } from '../../subcomponents/Modal';
import getBrand from '../utils/getBrand';
import { apiBaseUrl } from '../utils/getConfigs';
import { cleanBrandParam } from '../utils/getBrand';
import { translatePageData } from './translatePageData';
import { analytics } from '../utils/analytics';
import { getOffer } from "../utils/api-calls";
import { ErrorType, noticeError } from '../utils/newRelic';

@Component({
  tag: 'lead-generation',
  styleUrl: 'lead-generation.scss',
  shadow: true,
})
export class LeadGeneration {
  @Prop({ mutable: true }) brand: string = getBrand();
  @Prop() listingId: string = "";
  @Prop() listingData: string = "";
  @Prop() layout: string = "sellerPanel";
  @Prop() isDirector: boolean = false;

  @State() isVerified: boolean = false;
  @State() showAuthContent: boolean = false;
  @State() buttonType: string;
  @State() lazyLoadComponents: boolean = false;
  @Element() hostElement: HTMLElement;

  translatedPageData: any;
  googleCategoryField = this.brand === 'kbb' ? 'my-kbb' : 'my-at';
  strategy: string;

  @Watch('listingData')
  watchListingDataPropHandler(newValue: string) {
    const newListingData = translatePageData(newValue);

    if (newListingData.seller?.name !== "Private Seller Exchange") {
      if (this.isDirector && newListingData.seller?.consumerId !== this.translatedPageData.seller?.consumerId) {
        this.fetchSellerVerification(newListingData.seller?.consumerId || '');
      }

      this.translatedPageData = newListingData || {};
    }
  }

  @Watch('listingId')
  watchListingIdPropHandler() {
    this.emitOfferData();
  }

  closeModalHandler() {
    state.hideModal('sign-up-modal');
  }

  showSignUpContent(e: any) {
    analytics.SIGN_UP_CTA(e, { eventElement: this.googleCategoryField });
    this.showAuthContent = true;
  }

  private openModal = (modalId: string, e: any) => {
    analytics.SIGN_UP_INFO_LINK(e, { eventElement: this.googleCategoryField });
    this.showAuthContent = false;
    state.showModal(modalId);
  }

  emitOfferData = async () => {
    delete eventBus.getPsxOfferData;
    const offerData = await getOffer(this.listingId, apiBaseUrl, `Bearer ${state.cognitoIdToken}`).catch((e) => {
      if (this.listingId === e.listingId) {
        eventBus.getPsxOfferData = () => ({});
        eventBus.emit('psxOfferData', {});
      }
    });

    if (this.listingId === offerData?.listing?.id) {
      eventBus.getPsxOfferData = () => (offerData);
      eventBus.emit('psxOfferData', offerData);
    }
  }

  async fetchSellerVerification(consumerId: string) {
    try {
      if (consumerId) {
        const response = await fetch(`${apiBaseUrl}/api/psx-lact/user/risk-assessment?consumerId=${consumerId}`, {
          method: 'GET',
        });
        const consumerData = await response?.json();

        if (consumerData?.is_verified) {
          this.isVerified = consumerData.is_verified;
        } else {
          this.isVerified = false;
        }
      } else {
        this.isVerified = false;
      }
      eventBus.emit('psx.listingActions.sellerVerification', {
        isVerified: this.isVerified,
      });
      eventBus.getSellerVerification = () => ({isVerified: this.isVerified});
    } catch (error) {
      noticeError(error, ErrorType.UNKNOWN, {
        methodName: 'fetchSellerVerification',
      });
      console.log("Error occurred while fetching data.", error);
    }
  }

  updateStrategy() {
    if (window.outerWidth < 768) {
      this.strategy = 'fixed';
    } else {
      this.strategy = 'absolute';
    }
  }
  async componentWillLoad() {
    this.brand = cleanBrandParam(this.brand);
    this.buttonType = this.brand === 'kbb' ? 'tertiary' : 'secondary';
    this.translatedPageData = translatePageData(this.listingData) || {};

    if (this.layout === 'sellerPanel' || this.layout === 'uiLess') {
      await Promise.all([consumerAuth(this.hostElement), this.fetchSellerVerification(this.translatedPageData?.seller?.consumerId || '')]);
    }

    ['auth.signout', 'auth.signin'].forEach((eventName) => {
      eventBus.on(eventName, this.closeModalHandler);
    });

    if (this.layout === 'srpListing') {
      eventBus.on('psx.listingActions.openMakeOffer', (e) => {
        this.openMakeOfferModal(e);
      });
    }

    this.updateStrategy();

    window.addEventListener('resize', () => {
      this.updateStrategy();
    });
  }

  async componentDidLoad() {
    this.lazyLoadComponents = true;

    /*
     * Temporary injection to resolve issue with media gallery making inputs non-interactive
     */
    if (this.layout === 'mediaGallery') {
      document.querySelector('div[data-cmp="mediaGalleryModal"]').removeAttribute("tabIndex");
    }
    // ----------------------

    if (this.isDirector) {
      let segmentIdentifyEventData: object;

      if (state.isUserLoggedIn) {
        this.emitOfferData();

        const response = await fetch(`${apiBaseUrl}/api/psx-lact/user/psx-user`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${state.cognitoIdToken}`
          }
        });
        const psxUser = await response?.json();
        segmentIdentifyEventData = {
          eventType: 'identify',
          identifyUserId: psxUser?.deprecated_psx_user_id || '',
          userName: psxUser?.legal_first_name || '',
          email: psxUser?.email || '',
          zip: psxUser?.zip || '',
        }
      }

      if (!eventBus.hasLoadedPsxComponent && state.isUserLoggedIn) {
        analytics.SEND_SEGMENT(segmentIdentifyEventData);
        eventBus.hasLoadedPsxComponent = true;
      }

      if (this.layout === 'srpListing') {
        eventBus.emit('psx.listingActions.makeOfferModalIsLoaded', {});
      }

      onChange('isUserLoggedIn', () => {
        if (state.isUserLoggedIn) {
          this.emitOfferData();
          analytics.SEND_SEGMENT(segmentIdentifyEventData);
        } else {
          delete eventBus.getPsxOfferData;
          analytics.SEND_SEGMENT({
            eventType: 'reset',
          });
        }
      });
    }
  }

  async openTestDriveModal(e) {
    state.showModal('test-drive-modal');
    analytics.SEND_SEGMENT({
      id: e.listingId,
      eventType: 'uiless-test-drive-modal',
    });
  }

  async openMakeOfferModal(e) {
    if (this.layout === 'srpListing') {
      // manually assign to trigger listingId change handler
      this.listingId = e.listingId;
    }

    state.showModal('make-offer-content');
    analytics.SEND_SEGMENT({
      id: e.listingId,
      eventType: 'uiless-make-offer-modal',
    });
  }

  render() {
    const whySignUpContent = (
      <div class="why-signup-info">
        <p>Why sign up, you ask? Well, with an account, you can:</p>
        <div class="py-4 text-left">
          <div class="flex flex-row gap-4 py-3">
            <img
              src="https://www.autotrader.com/psx-static/main/psx-vdp/PSXGetExclusiveAccess.svg"
              alt=""
              width={48}
              height={48}
            />
            <p class="mb-0">
              <strong>Get exclusive access</strong>
              {' to vehicles for sale by owner on Private Seller Exchange.'}
            </p>
          </div>
          <div class="flex flex-row gap-4 py-3">
            <img
              src="https://www.autotrader.com/psx-static/main/psx-vdp/PSXChatWithSeller.svg"
              alt=""
              width={48}
              height={48}
            />
            <p class="mb-0">
              <strong>Chat with private sellers, </strong>
              {' schedule test drives, and make offers online.'}
            </p>
          </div>
          <div class="flex flex-row gap-4 py-3">
            <img
              src="https://www.autotrader.com/psx-static/main/psx-vdp/PSXBuyCarsOnline.svg"
              alt=""
              width={48}
              height={48}
            />
            <p class="mb-0">
              <strong>Buy cars online</strong>
              {' from the comfort of your couch with the innovative and hassle-free PSX buyers checkout process.'}
            </p>
          </div>
        </div>
        <p>Ready to elevate your experience? Sign up now and let the perks begin!</p>
      </div>
    );

    const signUpContent = (<div class="max-w-screen-md"><cai-authentication screen="signup" signInCustomBodyText="Sign in to chat with sellers, make offers, schedule test drives, and get updates about your favorite cars. Join a community of car enthusiasts; no bots, just genuine people who love cars like you do." signUpCustomBodyText="Sign up for a free account and chat with sellers, make offers, schedule test drives, and get updates about your favorite cars. Join a community of car enthusiasts; no bots, just genuine people who love cars like you do." /></div>);

    const modalFooter = (
      <div class="flex flex-row justify-end items-center gap-4">
        <div>
          <a class="text-link" href='#' onClick={() => { state.hideModal('sign-up-modal') }}>Cancel</a>
        </div>
        <div>
          <Button
            brand={this.brand}
            buttonText="Sign Up"
            buttonType="primary"
            onClick={(e) => this.showSignUpContent(e)}
          />
        </div>
      </div>
    );

    const modalContent = this.showAuthContent ? signUpContent : whySignUpContent;
    const modalTitle = !this.showAuthContent ? "Unlock a World of Benefits" : '';

    const { listing, seller } = this.translatedPageData || {};
    const { location: { address: { city = "", state: userState = "" } = {} } = {} } = listing || {};
    const { name = "" } = seller || {};
    const initials = name?.split(" ").map((word: string) => word.charAt(0).toUpperCase()).join("");

    const emitCloseMediaGallery = () => {
      eventBus.emit('vdp.mediaGallery.close');
    }

    return (
      <tailwind-css>
        <host class={`theme-${this.brand} font-skin`}>
          {this.layout == "sellerPanel" &&
            (<Fragment>
              <div class="text-sm psx-lead panel panel-info">
                <div class="flex flex-row gap-2 text-nowrap">
                  <div class="h-10 w-10 border-2 border-solid border-white rounded-full bg-blue-950">
                    <p class="font-bold leading-5 pl-2 pt-3 text-white mb-0">{initials}</p>
                  </div>
                  <div class="seller-details">
                    <p class="seller-name text-base font-bold mb-0">{name} - Private Seller</p>
                    <p class="seller-location text-base font-normal mb-0">{city && `${city},`} {userState}</p>
                  </div>
                  <div class="seller-details ">
                    {this.isVerified && (
                      <p class="id-verified-check text-green pt-0.5 text-xs inline-flex mb-0">
                        Identity Verified
                        <span class="pl-0.5 pt-1"><img
                          src="https://www.autotrader.com/psx-static/main/psx-vdp/PSXVerifiedSellerIcon.svg"
                          alt=""
                          width={12}
                          height={12}
                        />
                        </span>
                      </p>
                    )}
                  </div>
                  <div class="seller-details-small">
                    <p class="seller-name text-base font-bold mb-0">{name} - Private Seller</p>
                    {this.isVerified && (
                      <p class="id-verified-check text-green pt-0.5 text-xs inline-flex mb-0">
                        Identity Verified
                        <span class="pl-0.5 pt-1"><img
                          src="https://www.autotrader.com/psx-static/main/psx-vdp/PSXVerifiedSellerIcon.svg"
                          alt=""
                          width={12}
                          height={12}
                        />
                        </span>
                      </p>
                    )}
                    <p class="seller-location text-base font-normal mb-0">{city && `${city},`} {userState}</p>
                  </div>
                </div>
                <div class="mt-4 components-container">
                  {this.lazyLoadComponents && (
                    <Fragment>
                      {listing?.financingTypePSX.toLowerCase() !== "leased" && (
                        <div class="hide-make-offer-button min-w-full"><make-offer-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} shouldOfferDataUseBus={true} /></div>
                      )}

                      <div class="buyer-action-buttons">
                        <div class="action-button min-w-28"><chat-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={listing?.financingTypePSX.toLowerCase() !== "leased" ? this.buttonType : "primary"} /></div>
                        <div class="action-button min-w-28"><test-drive-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={this.buttonType} /></div>
                        </div>
                    </Fragment>
                  )}
                  {!state.isUserLoggedIn && (<p class="sign-up-info  text-center text-sm font-normal mt-2 mb-0">
                    <button class="text-link inline-flex" onClick={(e) => this.openModal('sign-up-modal', e)}>
                      Sign up to interact with sellers
                      <span class="pl-1 pt-0.5">{Icon.CircleIcon("i", "18", "#024aa5")}</span>
                    </button>
                  </p>)
                  }
                </div>
                <div class="mt-4 min-w-full lower-section-details">
                  <img src="https://www.autotrader.com/psx-static/main/psx-vdp/PSE-NoSeal.svg" width={100} alt="Private Seller Exchange" />
                  <div class="py-3">
                    <p class="font-bold mb-0">Test drive with Sellers. Buy cars online. Clean title. No hassle.</p>
                    <p class="font-normal min-h-11 min-[768]:min-h-16 max-[1299]:min-h-16">
                      Free AutoCheck report, fraud protection,&nbsp;
                      <span class="inline-flex min-h-5">
                        financing&nbsp;
                        <span class="pt-0.5"> <psx-tooltip
                          tooltipLabel={Icon.CircleIcon("i", "18", "#024aa5")}
                          tooltipContent="In some areas, financing may not be an option and only cash payments will be accepted."
                          placement="bottom"
                          tooltipPosition={this.strategy}
                        />
                        </span>
                      </span>
                      , vehicle service contracts and GAP insurance available
                    </p>
                  </div>
                  <p class="mb-0 min-h-7"><a class="text-link inline-flex" href="https://www.autotrader.com/marketplace/buyer-guide?analyticsSource=vdp" onClick={(e) => analytics.BUYER_GUIDE_LINK(e)} target="_blank"><span class="pr-0.5 pt-0.5">{Icon.CircleIcon("?", "18", "#024aa5")}</span> Questions? Review our Buyer Guide</a></p>
                </div>
              </div>
              <div>
              </div>
            </Fragment>)
          }
          {
            this.layout == "makeOffer" && listing?.financingTypePSX.toLowerCase() !== "leased" && (
              <make-offer-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} shouldOfferDataUseBus={true} />
            )
          }
          {
            this.layout == 'startPurchase' && listing?.financingTypePSX.toLowerCase() !== "leased" && (
              <div class="grid">
                <div class="justify-self-center sm:justify-self-start">
                    <make-offer-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} shouldOfferDataUseBus={true} />
                </div>
              </div>
              )
          }
          {
            this.layout == "mediaGallery" && (
              <div class="flex flex-row gap-2">
                  { listing?.financingTypePSX.toLowerCase() !== "leased" && (
                    <div onClick={emitCloseMediaGallery}><make-offer-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonSize="xs" shouldOfferDataUseBus={true} /></div>
                  )}
                  <div onClick={emitCloseMediaGallery}><chat-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={listing?.financingTypePSX.toLowerCase() !== "leased" ? this.buttonType : "primary"} buttonSize="xs" /></div>
                  <div onClick={emitCloseMediaGallery}><test-drive-button listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={this.buttonType} buttonSize="xs" /></div>
              </div>
            )
          }
          {
            this.layout == "sticky" && (
              <div class="flex flex-row gap-4">
                { listing?.financingTypePSX.toLowerCase() !== "leased" && (
                  <div class="basis-3/4 md:basis-1/2"><make-offer-button pixallProps={{label: 'make_off::sticky-header'}} gtmProps={{eventResult: 'cta::make-offer::sticky-header'}} listingId={this.listingId} data={this.translatedPageData} brand={this.brand} shouldOfferDataUseBus={true} /></div>
                )}
                <div class="basis-1/4 md:basis-1/2"><test-drive-button pixallProps={{label: 'test_drv::sticky-header'}} gtmProps={{eventResult: 'cta::open::test-drive::sticky-header'}} listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={this.buttonType} /></div>
              </div>
            )
          }
          {
            this.layout == "uiLess" && (
              <div>
              <chat-button
                layout='uiLess'
                listingId={this.listingId} data={this.translatedPageData} brand={this.brand} buttonType={this.buttonType}
              />

              {eventBus.on('psx.listingActions.openTestDrive', (e) => {
                this.openTestDriveModal(e);
              })}

              {eventBus.on('psx.listingActions.openMakeOffer', (e) => {
                this.openMakeOfferModal(e);
              })}

              <enhanced-modal
                modalId={'make-offer-content'}
                brand={this.brand}
                modalHeader={"Make an Offer"}
                actionType={'offer'}
                modalBody={
                  <make-offer-content
                    modalId={'make-offer-content'}
                    sendOfferButtonText={"Send Offer"}
                    showHighestVerifiedOffer={false}
                    listingId={this.listingId}
                    type={'vdp'}
                    brand={this.brand}
                    authToken={state.cognitoIdToken}
                    data={this.translatedPageData}
                  />
                }
              />

              <enhanced-modal
                modalId={'test-drive-modal'}
                actionType={'test-drive'}
                brand={this.brand}
                modalHeader={'Request Test Drive'}
                modalBody={
                  <test-drive
                    listingId={this.listingId}
                    authToken={state.cognitoIdToken}
                    data={this.translatedPageData}
                    brand={this.brand}
                  />
                }
              />
              </div>
            )
          }
          {
            this.layout == "srpListing" && (
              <enhanced-modal
                modalId={'make-offer-content'}
                brand={this.brand}
                modalHeader={"Make an Offer"}
                actionType={'offer'}
                modalBody={
                  <make-offer-content
                    modalId={'make-offer-content'}
                    sendOfferButtonText={"Send Offer"}
                    showHighestVerifiedOffer={false}
                    listingId={this.listingId}
                    type={'srp'}
                    brand={this.brand}
                    authToken={state.cognitoIdToken}
                  />
                }
              />
            )
          }
          {this.lazyLoadComponents && <BaseModalContainer
            modalId="sign-up-modal"
            brand={this.brand}
            modalHeader={modalTitle}
            modalBody={modalContent}
            modalFooter={!this.showAuthContent && modalFooter}
          ></BaseModalContainer>}
        </host>
      </tailwind-css>
    )
  }
}
